import React, { useEffect, useRef, useCallback } from 'react';
import {
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
} from '@mui/material';

export interface ReportNavigationItem {
  id: string;
  label: string;
  icon: React.ReactNode;
}

export interface ReportNavigationPanelProps {
  items: ReportNavigationItem[];
  activeSection: string;
  onSectionClick: (sectionId: string) => void;
}

// Helper function to get scroll metrics
const getScrollMetrics = (topOffset: number) => {
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const viewportHeight = window.innerHeight;
  const scrollBottom = scrollTop + viewportHeight;
  return { scrollTop, scrollBottom, viewportHeight, topOffset };
};

// Helper function to determine target section
const determineTargetSection = (
  items: ReportNavigationItem[],
  metrics: ReturnType<typeof getScrollMetrics>
): string | null => {
  const { scrollTop, topOffset } = metrics;
  const threshold = scrollTop + topOffset;

  // Find the section that is currently at or just above the threshold
  for (let i = items.length - 1; i >= 0; i--) {
    const element = document.getElementById(items[i].id);
    if (element) {
      const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
      if (elementTop <= threshold) {
        return items[i].id;
      }
    }
  }

  // If no section found, return the first one
  return items[0]?.id ?? null;
};

export const ReportNavigationPanel: React.FC<ReportNavigationPanelProps> = ({
  items,
  activeSection,
  onSectionClick,
}) => {
  const isNavigatingRef = useRef(false);
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSectionIdRef = useRef<string>(activeSection);

  // Handle scroll position detection
  useEffect(() => {
    const handleScroll = () => {
      if (isNavigatingRef.current) return;

      const topOffset = 100;
      const metrics = getScrollMetrics(topOffset);
      const targetSectionId = determineTargetSection(items, metrics);

      if (targetSectionId && targetSectionId !== lastSectionIdRef.current) {
        lastSectionIdRef.current = targetSectionId;
        onSectionClick(targetSectionId);
      }
    };

    // Initial check
    handleScroll();

    // Listen to scroll events with throttle
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        globalThis.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }
    };
  }, [items, onSectionClick]);

  const handleClick = useCallback(
    (sectionId: string) => {
      // Immediately update active section and ref
      onSectionClick(sectionId);
      lastSectionIdRef.current = sectionId;

      // Clear any existing timeout
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }

      // Find and scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        const topOffset = 100;

        // Disable scroll spy during navigation
        isNavigatingRef.current = true;

        const y = element.getBoundingClientRect().top + window.pageYOffset - topOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });

        // Re-enable scroll spy after scroll completes
        navigationTimeoutRef.current = setTimeout(() => {
          isNavigatingRef.current = false;
          navigationTimeoutRef.current = null;
        }, 1000);
      }
    },
    [onSectionClick]
  );

  return (
    <Paper
      elevation={2}
      sx={{
        position: 'sticky',
        top: 20,
        maxHeight: 'calc(100vh - 40px)',
        overflow: 'auto',
      }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant='h6' fontWeight={600}>
          入力項目
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          各セクションをクリックして移動
        </Typography>
      </Box>

      <List sx={{ p: 0 }}>
        {items.map((item) => {
          const isActive = activeSection === item.id;

          return (
            <ListItem key={item.id} disablePadding>
              <ListItemButton
                onClick={() => handleClick(item.id)}
                selected={isActive}
                sx={{
                  py: 1.5,
                  px: 2,
                  '&.Mui-selected': {
                    backgroundColor: '#D7E2FF',
                    borderRight: 3,
                    borderRightColor: '#0D2F60',
                    color: '#0D2F60',
                    '&:hover': { backgroundColor: '#D7E2FF' },
                  },
                }}
              >
                <ListItemIcon sx={{ color: isActive ? '#0D2F60' : 'text.secondary', minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>

                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? '#0D2F60 !important' : 'text.primary',
                    fontSize: '0.9rem',
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );
};
